import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Edit, ArrowLeft, Shield, ShieldCheck, ShieldAlert, Key, X, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { authFetch, getCurrentUser } from '../../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  directeur: { label: 'Directeur', color: 'bg-purple-100 text-purple-800', icon: ShieldCheck, desc: 'Acces complet' },
  marketing: { label: 'Marketing', color: 'bg-blue-100 text-blue-800', icon: Shield, desc: 'Blog, Medias, Stats' },
  commercial: { label: 'Commercial', color: 'bg-green-100 text-green-800', icon: ShieldAlert, desc: 'Preinscriptions, Rappels, Messages' },
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'commercial' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const fetchUsers = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/users`);
      if (res.status === 401 || res.status === 403) { navigate('/admin'); return; }
      setUsers(await res.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (currentUser.role !== 'directeur') { navigate('/admin'); return; }
    fetchUsers();
  }, [fetchUsers, navigate, currentUser.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await authFetch(`${API_URL}/api/admin/users/${editId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, name: form.name, role: form.role }),
        });
        toast.success('Utilisateur modifie');
      } else {
        const res = await authFetch(`${API_URL}/api/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
        toast.success('Utilisateur cree');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ email: '', name: '', password: '', role: 'commercial' });
      fetchUsers();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer l'utilisateur ${name} ?`)) return;
    try {
      const res = await authFetch(`${API_URL}/api/admin/users/${id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('Utilisateur supprime');
      fetchUsers();
    } catch (err) { toast.error(err.message); }
  };

  const handleResetPassword = async (id, name) => {
    if (!window.confirm(`Reinitialiser le mot de passe de ${name} ?`)) return;
    try {
      const res = await authFetch(`${API_URL}/api/admin/users/${id}/reset-password`, { method: 'PUT' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Nouveau mot de passe : ${data.new_password}`, { duration: 15000 });
    } catch (err) { toast.error(err.message); }
  };

  const openEdit = (user) => {
    setEditId(user.id);
    setForm({ email: user.email, name: user.name, password: '', role: user.role });
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-users-page">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard"><Button variant="ghost" size="sm"><ArrowLeft size={18} className="mr-2" /> Dashboard</Button></Link>
            <div className="flex items-center gap-2"><Users className="text-[#003366]" size={24} /><h1 className="text-xl font-bold text-[#003366]">Gestion des utilisateurs</h1></div>
          </div>
          <Button onClick={() => { setShowForm(true); setEditId(null); setForm({ email: '', name: '', password: '', role: 'commercial' }); }} className="bg-[#003366] hover:bg-[#002244] rounded-full" data-testid="add-user-btn"><Plus size={18} className="mr-2" /> Nouvel utilisateur</Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
            <div key={key} className="bg-white rounded-xl border p-4 flex items-start gap-3">
              <div className={`p-2 rounded-lg ${cfg.color}`}><cfg.icon size={20} /></div>
              <div><h3 className="font-bold text-[#003366]">{cfg.label}</h3><p className="text-sm text-gray-500">{cfg.desc}</p></div>
            </div>
          ))}
        </div>
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" data-testid="user-form-modal">
              <div className="flex items-center justify-between mb-6"><h2 className="text-lg font-bold text-[#003366]">{editId ? 'Modifier' : 'Nouvel'} utilisateur</h2><button onClick={() => { setShowForm(false); setEditId(null); }}><X size={20} /></button></div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Nom complet</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet" required data-testid="user-name-input" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@cefat-bs.ci" required data-testid="user-email-input" /></div>
                {!editId && <div><Label>Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 caracteres" required data-testid="user-password-input" /></div>}
                <div>
                  <Label>Role</Label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-200" data-testid="user-role-select">
                    <option value="directeur">Directeur (acces complet)</option>
                    <option value="marketing">Marketing (blog, medias, stats)</option>
                    <option value="commercial">Commercial (preinscriptions, rappels, messages)</option>
                  </select>
                </div>
                <Button type="submit" disabled={saving} className="w-full bg-[#003366] hover:bg-[#002244] rounded-full" data-testid="user-save-btn">
                  {saving ? <Loader2 className="mr-2 animate-spin" size={18} /> : <Save size={18} className="mr-2" />}{editId ? 'Modifier' : 'Creer'}
                </Button>
              </form>
            </div>
          </div>
        )}
        {loading ? <div className="text-center py-12 text-gray-500">Chargement...</div> : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead><tr className="bg-gray-50 border-b">
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Utilisateur</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Role</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-600">Actions</th>
              </tr></thead>
              <tbody>
                {users.map(u => {
                  const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.commercial;
                  return (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-9 h-9 bg-[#003366] rounded-full flex items-center justify-center text-white font-bold text-sm">{u.name.charAt(0).toUpperCase()}</div><span className="font-medium text-[#003366]">{u.name}</span></div></td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                      <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span></td>
                      <td className="px-6 py-4"><div className="flex items-center gap-2">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" title="Modifier" data-testid={`edit-user-${u.id}`}><Edit size={18} /></button>
                        <button onClick={() => handleResetPassword(u.id, u.name)} className="p-1.5 rounded-lg hover:bg-yellow-50 text-yellow-600" title="Reinitialiser mdp" data-testid={`reset-pwd-${u.id}`}><Key size={18} /></button>
                        {u.id !== currentUser.id && <button onClick={() => handleDelete(u.id, u.name)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" title="Supprimer" data-testid={`delete-user-${u.id}`}><Trash2 size={18} /></button>}
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
